import assets from "../../assets";
import { useState, useEffect, useRef } from "react";
import { useApi } from "../../hooks/use-api";
import { useSingleton } from "../../hooks/use-singleton";
import { COOKIE_ACCESS_MAIL } from "../../utils/storage-constants";
import {
  apiTeacherStats,
  API_ADMIN_BANKING_SUBJECTS,
  API_ADMIN_STUDENT_SUBJECTS,
} from "../../utils/api-routes";
import {
  ROUTE_TEST_MANAGEMENT,
  ROUTE_TEST_CREATION,
  ROUTE_STUDENT_PERFORMANCE,
  ROUTE_STUDENT_LEADERBOARD,
  ROUTE_KNOWLEDGE_BASE,
  ROUTE_QUESTION_PAPERS,
} from "../../route/teacherRoute";

const useHandler = () => {
  const { post, get } = useApi();
  const { user } = useSingleton();
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchIdRef = useRef(0); // tracks the latest fetch

  const fetchStats = async (currentUser) => {
    const fetchId = ++fetchIdRef.current;
    setStatsData(null);
    const email =
      currentUser?.email ||
      currentUser?.data?.email ||
      (typeof window !== "undefined" &&
        (localStorage.getItem(COOKIE_ACCESS_MAIL) ||
          localStorage.getItem("email") ||
          localStorage.getItem("user_email") ||
          localStorage.getItem("__FANAMA_USER_EMAIL__"))) ||
      "";

    if (!email) {
      setLoading(false);
      return;
    }

    try {
      const endpoint = `/api/teacher/dashboard?teacher_email=${encodeURIComponent(email)}`;
      const response = await get(endpoint);

      if (fetchId === fetchIdRef.current && response?.data) {
        setStatsData(response.data);
      }
    } catch (error) {
      console.error("Error fetching teacher dashboard stats:", error);
      // Fallback to previous endpoint if available
      try {
        const currentExamType = currentUser?.course || currentUser?.exam || currentUser?.board;
        if (currentExamType) {
          let filterValue = currentUser?.subject || "";
          const extraParams = { teacher_mail: email };
          const fallbackRes = await get(apiTeacherStats(currentExamType, filterValue, extraParams));
          if (fetchId === fetchIdRef.current && fallbackRes?.data) {
            setStatsData(fallbackRes.data);
          }
        }
      } catch (fallbackErr) {
        if (fetchId === fetchIdRef.current) {
          console.error("Error fetching fallback stats:", fallbackErr);
        }
      }
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchStats(user?.data);
    }, 100);

    return () => clearTimeout(timer);
  }, [user?.data?.email]);

  const formatAvgScore = (val) => {
    if (val === undefined || val === null) return "0%";
    const strVal = val.toString();
    return strVal.endsWith("%") ? strVal : `${strVal}%`;
  };

  const statsCards = [
    {
      title: statsData?.total_students !== undefined ? "Total Students" : "Total Marks",
      value: statsData?.total_students !== undefined
        ? statsData.total_students.toString()
        : (statsData?.total_marks_scored?.toString() || "0"),
      icon: assets.ic_total_student,
      iconBg: "#F5F3FF",
      bgColor: "#F5F3FF",
    },
    {
      title: "Test Created",
      value: statsData?.tests_created !== undefined
        ? statsData.tests_created.toString()
        : statsData?.tests_attended !== undefined
          ? statsData.tests_attended.toString()
          : (statsData?.tests_created?.toString() || statsData?.tests_attended?.toString() || "0"),
      icon: assets.ic_test_attend,
      iconBg: "#F0FDF4",
      bgColor: "#F0FDF4",
    },
    {
      title: "Average Score",
      value: statsData?.average_score !== undefined
        ? formatAvgScore(statsData.average_score)
        : (statsData?.percentage ? `${statsData.percentage}%` : "0%"),
      icon: assets.ic_average_score,
      iconBg: "#FFFBEB",
      bgColor: "#FFFBEB",
    },
    {
      title: statsData?.questions_in_bank !== undefined ? "Questions in Bank" : "Maximum Marks",
      value: statsData?.questions_in_bank !== undefined
        ? statsData.questions_in_bank.toString()
        : (statsData?.total_max_marks?.toString() || "0"),
      icon: assets.ic_question_blue,
      iconBg: "#EFF6FF",
      bgColor: "#EFF6FF",
    },
  ];

const pathCards = [
  {
    title: "Student Performance",
    description: "Track student progress, scores, attendance, and learning trends in detail.",
    action: "View Analytics",
    icon: assets.ic_student_orange,
    bgColor: "rgba(255, 111, 0, 0.10)",
    route: ROUTE_STUDENT_PERFORMANCE,
  },
  {
    title: "Question Papers",
    description: "Create, organize, and manage question papers with ease.",
    action: "Manage Question Papers",
    icon: assets.ic_question_grey,
    bgColor: "rgba(128, 201, 93, 0.10)",
    route: ROUTE_QUESTION_PAPERS,
  },
  {
    title: "Test Management",
    description: "Create, schedule, and evaluate student assessments.",
    action: "Manage Tests",
    icon: assets.ic_manage_test,
    bgColor: "rgba(60, 135, 208, 0.10)",
    route: ROUTE_TEST_MANAGEMENT,
  },
  {
    title: "Test Creation",
    description: "Design custom test papers, set duration, choose questions, and publish tests.",
    action: "Create Test",
    icon: assets.ic_manage_test,
    bgColor: "rgba(147, 51, 234, 0.10)",
    route: ROUTE_TEST_CREATION,
  },
  {
    title: "Leaderboard",
    description: "View student rankings and recognize top performers.",
    action: "View Leaderboard",
    icon: assets.ic_leader_yellow,
    bgColor: "rgba(255, 220, 11, 0.10)",
    route: ROUTE_STUDENT_LEADERBOARD,
  },
  {
    title: "Knowledge Base",
    description: "Upload and manage learning resources in one place.",
    action: "Open Knowledge Base",
    icon: assets.ic_knowledge_pink,
    bgColor: "rgba(192, 58, 43, 0.10)",
    route: ROUTE_KNOWLEDGE_BASE,
  }
];

return {
  statsCards,
  pathCards,
};
}

export default useHandler
